import { useState } from 'react';
import { createAdditionalContent, updateAdditionalContent } from '../../services/content/additionalContentService';
import { ImageUploadField } from './ImageUploadField';
import type { AdditionalContent, AdditionalContentLevel, ContentType } from '../../types/content/additionalContent';

const levelOptions: AdditionalContentLevel[] = ['beginner', 'intermediate', 'advanced'];
const contentTypeOptions: ContentType[] = ['video', 'document', 'webpage', 'image'];

interface AdditionalContentModalProps {
  content?: AdditionalContent;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdditionalContentModal({ content, onClose, onSuccess }: AdditionalContentModalProps) {
  const isEdit = !!content;

  const [title, setTitle] = useState(content?.title || '');
  const [description, setDescription] = useState(content?.description || '');
  const [level, setLevel] = useState<string>(content?.level || 'beginner');
  const [contentType, setContentType] = useState<string>(content?.contentType || 'video');
  const [instructor, setInstructor] = useState(content?.instructor || '');
  const [videoUrl, setVideoUrl] = useState(content?.videoUrl || '');
  const [html, setHtml] = useState(content?.html || '');
  const [page, setPage] = useState(content?.page || '');
  const [slot, setSlot] = useState(content?.slot || '');
  const [order, setOrder] = useState<string>(content?.order != null ? String(content.order) : '');
  const [imageUrl, setImageUrl] = useState(content?.imageUrl || '');
  const [instructorAvatarUrl, setInstructorAvatarUrl] = useState(content?.instructorAvatarUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  async function handleSave() {
    if (!title.trim()) return;
    setError('');
    setLoading(true);
    try {
      const data = {
        title: title.trim(),
        description: description.trim(),
        level: level as AdditionalContentLevel,
        contentType: contentType as ContentType,
        instructor: instructor.trim(),
        videoUrl: videoUrl.trim() || null,
        html: html.trim() || null,
        page: page.trim() || null,
        slot: slot.trim() || null,
        order: order.trim() ? parseInt(order, 10) : null,
      };
      if (isEdit) {
        await updateAdditionalContent(content!.id, { ...content!, ...data });
      } else {
        await createAdditionalContent(data);
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update content' : 'Failed to create content');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {isEdit ? 'Edit Additional Content' : 'Add Additional Content'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Title *</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} placeholder="Content title" />
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} rows={3} placeholder="Content description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Level</label>
                <select value={level} onChange={(e) => setLevel(e.target.value)} className={inputClass}>
                  {levelOptions.map((l) => (
                    <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Content Type</label>
                <select value={contentType} onChange={(e) => setContentType(e.target.value)} className={inputClass}>
                  {contentTypeOptions.map((ct) => (
                    <option key={ct} value={ct}>{ct.charAt(0).toUpperCase() + ct.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Instructor</label>
              <input type="text" value={instructor} onChange={(e) => setInstructor(e.target.value)} className={inputClass} placeholder="Instructor name" />
            </div>
            <div>
              <label className={labelClass}>Video URL</label>
              <input type="text" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} className={inputClass} placeholder="https://..." />
            </div>
            <div>
              <label className={labelClass}>HTML Content</label>
              <textarea value={html} onChange={(e) => setHtml(e.target.value)} className={inputClass} rows={3} placeholder="HTML content" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Page</label>
                <input type="text" value={page} onChange={(e) => setPage(e.target.value)} className={inputClass} placeholder="e.g. home" />
              </div>
              <div>
                <label className={labelClass}>Slot</label>
                <input type="text" value={slot} onChange={(e) => setSlot(e.target.value)} className={inputClass} placeholder="e.g. market_pulse" />
              </div>
              <div>
                <label className={labelClass}>Order</label>
                <input type="number" value={order} onChange={(e) => setOrder(e.target.value)} className={inputClass} placeholder="0" />
              </div>
            </div>
            {isEdit && content?.id && (
              <>
                <ImageUploadField
                  label="Content Image"
                  currentUrl={imageUrl}
                  uploadEndpoint={`/additional-content/${content.id}/image`}
                  onUploaded={() => onSuccess()}
                />
                <ImageUploadField
                  label="Instructor Avatar"
                  currentUrl={instructorAvatarUrl}
                  uploadEndpoint={`/additional-content/${content.id}/instructor-avatar`}
                  onUploaded={() => onSuccess()}
                />
              </>
            )}
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading || !title.trim()} className={btnPrimary}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
